import axios from "axios";


export interface LocationCoordinate {
  latitude: number;
  longitude: number;
}

export interface GeocodingResult {
  address: string;
  city?: string;
  country?: string;
  postalCode?: string;
  formattedAddress?: string;
  placeId?: string;
}

const API_KEY = process.env.REACT_APP_GOOGLE_MAPS_APIKEY;

/**
 * Fonction pour formater le résultat du géocodage inverse
 */
export const formatGeocoderResult = (result: any): GeocodingResult => {
  // Vérifier si le résultat existe et a une propriété geometry
  if (!result || !result.geometry) {
    console.warn("Invalid geocoder result received:", result);
    return { address: "Adresse inconnue" };
  }

  // Extraire les composants d'adresse
  let city = "";
  let postalCode = "";
  let country = "";

  if (result.address_components) {
    for (const component of result.address_components) {
      const types = component.types;

      if (types.includes("locality")) {
        city = component.long_name;
      } else if (types.includes("postal_code")) {
        postalCode = component.long_name;
      } else if (types.includes("country")) {
        country = component.long_name;
      }
    }
  }

  return {
    address: result.formatted_address || "Adresse inconnue",
    city,
    country,
    postalCode,
    formattedAddress: result.formatted_address,
    placeId: result.place_id,
  };
};

/**
 * Fonction pour obtenir une adresse à partir de coordonnées
 */
export const reverseGeocoding = async (
  latitude: number,
  longitude: number
): Promise<GeocodingResult> => {
    try {
    if (!latitude || !longitude) {
      throw new Error("Latitude and longitude are required");
    }

    if (!API_KEY) {
      console.error("REACT_APP_GOOGLE_MAPS_APIKEY is not set in environment variables");
      return { address: "API key not configured" };
    }

    const url = `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=${API_KEY}`;
    const response = await axios.get(url);

    // Vérifier si la réponse contient des résultats
        if (
      response.data &&
      response.data.status === "OK" &&
      response.data.results &&
      response.data.results.length > 0
    ) {
      return formatGeocoderResult(response.data.results[0]);
    }

    console.warn("Geocoding API returned no results:", response.data);
    return { address: "Adresse non trouvée" };
  } catch (error: any) {
    console.error("Error during reverse geocoding:", error);
    return { address: "Erreur de géocodage" };
  }
};

/**
 * Fonction pour obtenir des coordonnées à partir d'une adresse
 */
export const forwardGeocoding = async (
  address: string
): Promise<LocationCoordinate | null> => {
  try {
    if (!address) {
      throw new Error("Address is required");
    }

    const url = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(
      address
    )}&key=${API_KEY}`;
    const response = await axios.get(url);

    if (
      response.data &&
      response.data.status === "OK" &&
      response.data.results &&
      response.data.results.length > 0 &&
      response.data.results[0].geometry &&
      response.data.results[0].geometry.location
    ) {
      const location = response.data.results[0].geometry.location;
      return {
        latitude: location.lat,
        longitude: location.lng,
      };
    }

    console.warn("Forward geocoding API returned no results:", response.data);
    return null;
  } catch (error) {
    console.error("Error during forward geocoding:", error);
    return null;
  }
};
